/**
 * @fileOverview Render a component that fetches a list of items asynchonously.
 * Useful for paging through data and displaying it however you want with
 * a standard paging UI, with filters and a search. Requires an
 * asynchronous data source for the data. Makes assumptions about
 * a rough convention that is followed. Useful if you want to render data
 * from a graphQL endpoint with minimal boilerplate.
 */

import React, { Fragment, useState, useEffect } from 'react'

const DEFAULT_MAX_RECORDS = 1000

/**
 * @typedef FetchDataArgs
 * @property {number} first How many records it is requesting to return. So if
 * this value is 20 it expects a result returned with 20 records.
 * @property {number} skip How many records to skip given the current fetch
 * criteria, so if this value is 40, then skip the first 40 records.
 * @property {string} filter Filter search text to filter results by. This is
 * the value the user will have entered into the search field.
 * @property {string} orderBy Depending on which table header was selected this
 * will return the label for that header with spaces removed, uppercased and
 * `_ASC` or `_DESC` appended. For example if the header is `Time of Day`. And
 * that header is clicked, this would return `TIMEOFDAY_ASC`. If it was clicked
 * a second time it would return `TIMEOFDAY_DESC`.
 *
 * @typedef DataFetchingFilterOption
 * @property {string} label The label to display in the filter option
 * @property {Mixed} value The value to pass back to the fetchData function.
 * Usually a database id but can be anything.
 *
 * @typedef DataFetchingFilter
 * @property {string} label This is the caption that's displayed next to the
 * filter
 * @property {Array<DataFetchingFilterOption>} options The filter options
 *
 * @typedef DataFetchingComponentProps
 * @property {Array<DataFetchingFilter>} filters The filters options
 * to show against the table.
 * For example
 * ```
 * [
 *   {
 *     label: 'Status',
 *     options: [
 *       { label: 'Draft', value: 'DRAFT' },
 *       { label: 'Available', value: 'AVAILABLE' },
 *     ],
 *   },
 *   {
 *     label: 'Client',
 *     options: [
 *       { label: 'Woolworths', value: 2 },
 *       { label: 'Uber', value: 1, },
 *     ]
 *   }
 * ]
 * ```
 * @property {Function} fetchData This gets the component's data exactly as it
 * should be rendered. It will be called with the first parameter being an
 * object of type `FetchDataArgs` (see type for more info). Additionally to
 * the arguments specified in the `FetchDataArgs` type it will also include
 * properties that match the filter labels if any value for that filter is
 * selected. e.g. If you had a filter with label 'Client' and the value of the
 * selected option in the filter was the client id then the args object will
 * have the 'Client' property with the client id as the value. Use this to
 * make your filters filter the searched for data. It also must return an
 * object with the props `data` and `totalCount`. You can also return an
 * `actions` array, this should be an array of functions that will be
 * fired when a row is tapped or clicked.
 * For example.
 * ```
 *  var data = await getMissions({
 *    // Passes the first and skip params to our method to limit results
 *    //returned
 *    ...filters,
 *    clientId: filters['Client'],
 *  })
 *  var result = await response.json()
 *  return {
 *    // This `structureData` function will put the various mission properties
 *    // into a multi dimensional array ready for rendering.
 *    data: structureData(data.missions),
 *    // We also need to indicate how many more records there are, you will
 *    // need to make your backend account for this without returning the
 *     // entire data set.
 *    totalCount: data.totalCount
 *  }
 * ```
 * @property {boolean} showSearch Default is false
 * @property {function} refetch A function that this component calls on re-render,
 * which triggers a data refetch if it returns a truthy value. This gives
 * subcomponents a method to trigger refetches if the data source changes.
 * @property {boolean} enableFilterSuggestions Passed through to the
 * SearchAndFilters component, allows you to type in the search box to select
 * filter options via auto complete
 * @property {Function} Component This is the component that will be rendered.
 * This is quite low level, it will get passed a lot of options to use for
 * rendering. Perhaps take a look at DataFetchingTableTable for something
 * that works out the box.
 *
 * @param {DataFetchingComponentProps} props
 */
const DataFetchingComponent = props => {
  var {
    filters,
    fetchData: fetchDataOriginal,
    showSearch,
    refetch,
    showPages = true,
    enableFilterSuggestions,
    refreshRequestTrigger,
    Component,
    resultsPerPage: resultsPerPageProps,
    hasEditableAmountItemsPerPage,
    hasVerticalScroll,
    ...otherProps
  } = props
  // This is kind of some hooks Voodoo so that we can handle the race condition
  // issue internally to this component. This wraps the fetch method in a
  // wrapper function that ensures the last result is always used.
  const fetchData = takeLastResponse(fetchDataOriginal)

  const [data, setData] = useState([])
  const [actions, setActions] = useState([])
  const [disableActionColumnIndexes, setDisableActionColumnIndexes] = useState(
    [],
  )
  const [highlightColumnsIndexes, setHighlightColumnsIndexes] = useState([])
  const [footer, setFooter] = useState([])
  // Will be set to true if the current set of data has been requested
  const [requestedData, setRequestedData] = useState(false)

  var defaultSelectedFilters = {}
  filters.forEach(
    ({ label, selectedValue }) =>
      (defaultSelectedFilters[label] = selectedValue || null),
  )
  const [selectedFilters, setSelectedFilters] = useState(defaultSelectedFilters)

  // Required data for pagination
  const [totalRecords, setTotalRecords] = useState(null)
  const [offsetRecordsStart, setOffsetRecordsStart] = useState(0)
  const [resultsPerPage, setResultsPerPage] = useState(
    resultsPerPageProps || 50,
  )
  if (showPages === false) {
    setResultsPerPage(DEFAULT_MAX_RECORDS)
  }
  // This indicates we're currently fetching data
  const [loadingData, setLoadingData] = useState(true)
  // The search text
  const [searchText, setSearchText] = useState('')
  // The selected sort, default to by date desc
  const [selectedSort, setSelectedSort] = useState(null)

  // Give subclasses the opportunity to trigger data refetches
  useEffect(() => {
    if (refetch && refetch()) setRequestedData(false)
  })

  // This affect fetches all the mission data to display in the table rows
  // It stores the cell data in arrays within the `data` prop. If the filters
  // are changed and would consequently change the data being displayed then
  // set the `requestedData` prop to `false`.
  useEffect(() => {
    setRequestedData(true)
    var args = {}
    Object.keys(selectedFilters).forEach(label => {
      var filter = filters.find(f => f.label == label)
      var option = filter.options.find(o => o.label == selectedFilters[label])
      if (option) args[label] = option.value
      if (label == 'startDate' || label == 'endDate') {
        args[label] = selectedFilters[label]
      }
    })
    args['first'] = resultsPerPage
    args['skip'] = offsetRecordsStart
    args['filter'] = searchText.trim()
    args['orderBy'] = selectedSort
    ;(async () => {
      setLoadingData(true)
      const {
        data,
        totalCount,
        actions,
        footer,
        disableActionColumnIndexes,
        highlightColumnsIndexes,
      } = await fetchData(args)

      setData(data)
      setTotalRecords(totalCount)
      setActions(actions)
      setDisableActionColumnIndexes(disableActionColumnIndexes)
      setHighlightColumnsIndexes(highlightColumnsIndexes)
      setFooter(footer)
      // Set this after we've set all the data
      setLoadingData(false)
    })()
  }, [
    offsetRecordsStart,
    selectedFilters,
    totalRecords,
    searchText,
    selectedSort,
    // This is now only used by the refetch hook
    requestedData,
    refreshRequestTrigger,
    resultsPerPage,
  ])

  // Resets the flag the prevents refetching data, useful to use with methods
  // that change data filters
  const bindDataReset = func => (...args) => {
    func(...args)
    // Reset the paging
    setOffsetRecordsStart(0)
  }
  // Map filters to the format the TableTable is expecting
  var mappedFilters = filters.map(({ label, options, sorted }) => {
    return {
      label,
      values: options.map(o => o.label),
      selectedValue: selectedFilters[label],
      onChange: bindDataReset(newVal => {
        setSelectedFilters(prev => ({
          ...prev,
          [label]: newVal == null ? null : newVal,
        }))
      }),
      sorted,
    }
  })

  const onOffsetChange = offset => {
    if (offset < 0) offset = 0
    if (offset > totalRecords) return
    setOffsetRecordsStart(offset)
  }

  return (
    <Fragment>
      <Component
        filters={mappedFilters}
        data={data}
        actions={actions}
        disableActionColumnIndexes={disableActionColumnIndexes}
        highlightColumnsIndexes={highlightColumnsIndexes}
        totalRecords={totalRecords}
        offsetRecordsStart={offsetRecordsStart}
        onOffsetChange={onOffsetChange}
        resultsPerPage={resultsPerPage}
        setResultsPerPage={setResultsPerPage}
        hasEditableAmountItemsPerPage={hasEditableAmountItemsPerPage}
        loadingData={loadingData}
        searchText={showSearch ? searchText : undefined}
        onSearchTextChange={
          showSearch ? bindDataReset(setSearchText) : undefined
        }
        footer={footer}
        showPages={showPages}
        enableFilterSuggestions={enableFilterSuggestions}
        setSelectedSort={setSelectedSort}
        selectedSort={selectedSort}
        setOffsetRecordsStart={setOffsetRecordsStart}
        hasVerticalScroll={hasVerticalScroll}
        {...otherProps}
      />
      {showPages === false && data.length >= DEFAULT_MAX_RECORDS ? (
        <strong>Results capped at {DEFAULT_MAX_RECORDS}</strong>
      ) : null}
    </Fragment>
  )
}

export default DataFetchingComponent

/**
 * Ensures if multiple requests are fired before any are returned, only the
 * result from the last request is used.
 */
var takeLastResponse = function(fetchData) {
  var activePromise = null
  return (...args) => {
    var p = Promise.resolve(fetchData(...args))
    activePromise = p
    return p.then(() => activePromise)
  }
}



// WEBPACK FOOTER //
// ./src/Components/DataFetchingComponent/index.js